/**
 * Simple Product Shipping Calculator
 *
 * A completely independent shipping calculator for product pages that:
 * - Uses temporary cart operations with complete cleanup
 * - Validates 6-digit pincodes
 * - Handles shipping rate API calls independently
 * - Does not interfere with existing cart functionality
 */

class SimpleProductShippingCalculator {
  constructor(containerId) {
    this.container = document.getElementById(containerId);
    this.isCalculating = false;
    this.currentRequest = null;

    // Bind methods to maintain context
    this.calculateShipping = this.calculateShipping.bind(this);
    this.handlePincodeInput = this.handlePincodeInput.bind(this);

    this.init();
  }

  init() {
    if (!this.container) {
      console.warn("Simple shipping calculator container not found");
      return;
    }

    // Check if this is an E-invitation product (digital product that doesn't need shipping)
    if (this.isEInvitationProduct()) {
      console.log('E-invitation product detected - hiding shipping calculator');
      this.container.style.display = 'none';
      return;
    }

    this.bindEvents();
    this.updateCalculateButtonState();

    // Add emergency reset function for stuck loading states
    window.resetShippingCalculator = () => {
      console.log("Emergency reset of shipping calculator");
      this.setLoadingState(false);
      this.currentRequest = null;
      this.isCalculating = false;
    };

    console.log("Simple Product Shipping Calculator initialized");
    console.log("If button gets stuck, run: resetShippingCalculator()");
  }

  bindEvents() {
    const form = this.container.querySelector(".simple-shipping-form");
    const pincodeInput = this.container.querySelector(".pincode-input");
    const calculateBtn = this.container.querySelector(
      ".calculate-shipping-btn"
    );

    if (form) {
      form.addEventListener("submit", this.calculateShipping);
    }

    if (pincodeInput) {
      pincodeInput.addEventListener("input", this.handlePincodeInput);
      pincodeInput.addEventListener("keypress", (e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          this.calculateShipping(e);
        }
      });
    }

    if (calculateBtn) {
      calculateBtn.addEventListener("click", this.calculateShipping);
    }

    // Listen for quantity changes on the product form
    this.bindQuantityChangeEvents();
  }

  bindQuantityChangeEvents() {
    // Listen for quantity changes on the product form
    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (productForm) {
      const quantityInput = productForm.querySelector('input[name="quantity"]');
      if (quantityInput) {
        quantityInput.addEventListener("change", () => {
          console.log("Quantity changed to:", quantityInput.value);
          this.clearPreviousResults();
          this.updateCalculateButtonState();
        });

        quantityInput.addEventListener("input", () => {
          console.log("Quantity input changed to:", quantityInput.value);
          this.clearPreviousResults();
          this.updateCalculateButtonState();
        });
      }
    }
  }

  clearPreviousResults() {
    const resultsElement = this.container.querySelector(".shipping-results");
    if (resultsElement && resultsElement.innerHTML.trim() !== "") {
      const pincodeInput = this.container.querySelector(".pincode-input");

      // Show a message that results need to be recalculated if pincode is valid
      if (
        pincodeInput &&
        pincodeInput.value &&
        this.isValidPincode(pincodeInput.value)
      ) {
        const productData = this.getProductData();
        const quantity = productData ? productData.quantity : 1;

        resultsElement.innerHTML = `
          <div class="shipping-recalculate-notice">
            <p>Quantity updated to ${quantity} items. Click "Check" to recalculate shipping.</p>
          </div>
        `;
      } else {
        resultsElement.innerHTML = "";
      }
    }
  }

  handlePincodeInput(event) {
    const input = event.target;
    let value = input.value.replace(/\D/g, ""); // Remove non-digits

    // Limit to 6 digits
    if (value.length > 6) {
      value = value.substring(0, 6);
    }

    input.value = value;

    // Clear any previous error messages when user starts typing
    this.clearErrorMessages();

    // Provide real-time validation feedback
    this.showInputValidationFeedback(value);

    // Update button state
    this.updateCalculateButtonState();
  }

  showInputValidationFeedback(value) {
    const helpElement = this.container.querySelector(".pincode-help");
    if (!helpElement) return;

    if (value.length === 0) {
      helpElement.textContent =
        "Enter 6-digit pincode to check delivery options";
      helpElement.className = "pincode-help";
    } else if (value.length < 6) {
      helpElement.textContent = `Enter ${6 - value.length} more digit${
        6 - value.length > 1 ? "s" : ""
      }`;
      helpElement.className = "pincode-help validation-pending";
    } else if (value.length === 6) {
      helpElement.textContent =
        'Pincode looks good! Click "Check" to continue';
      helpElement.className = "pincode-help validation-success";
    }
  }

  clearErrorMessages() {
    const resultsElement = this.container.querySelector(".shipping-results");
    if (resultsElement) {
      // Only clear error messages, not successful results
      const errorElement = resultsElement.querySelector(".shipping-error");
      if (errorElement) {
        resultsElement.innerHTML = "";
      }
    }
  }

  updateCalculateButtonState() {
    const pincodeInput = this.container.querySelector(".pincode-input");
    const calculateBtn = this.container.querySelector(
      ".calculate-shipping-btn"
    );

    if (pincodeInput && calculateBtn) {
      const isValid = this.isValidPincode(pincodeInput.value);
      calculateBtn.disabled = !isValid || this.isCalculating;
    }
  }

  isValidPincode(pincode) {
    return /^\d{6}$/.test(pincode);
  }

  async calculateShipping(event) {
    event.preventDefault();

    if (this.isCalculating) {
      return;
    }

    const pincodeInput = this.container.querySelector(".pincode-input");
    const pincode = pincodeInput?.value?.trim();

    if (!this.isValidPincode(pincode)) {
      this.displayError(
        "Please enter a valid 6-digit pincode",
        "invalid_pincode"
      );
      return;
    }

    // Get product data fresh each time (don't cache)
    const productData = this.getProductData();
    if (!productData) {
      this.displayError(
        "Unable to get product information. Please refresh the page and try again.",
        "general"
      );
      this.setLoadingState(false);
      return;
    }

    console.log(
      "🔍 Starting shipping calculation with product data:",
      productData
    );

    // Use exact quantity without any validation or limits
    const calculationQuantity = productData.quantity;

    console.log("✅ Using exact quantity for shipping:", calculationQuantity);

    this.setLoadingState(true);

    try {
      // Try calculation with retry logic
      const maxRetries = 2;
      let lastError = null;

      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          const shippingRates = await this.fetchShippingRatesWithTempCart(
            productData.variantId,
            calculationQuantity,
            pincode
          );

          if (shippingRates && shippingRates.length > 0) {
            this.displayResults(shippingRates, pincode, calculationQuantity);
            return; // Success, exit retry loop
          } else {
            this.displayNoShippingMessage(pincode, calculationQuantity);
            return; // No shipping available, but not an error
          }
        } catch (error) {
          lastError = error;

          // Don't retry for certain types of errors
          if (
            error.name === "AbortError" ||
            error.message.includes("quantity") ||
            error.message.includes("not available") ||
            attempt === maxRetries
          ) {
            break; // Exit retry loop
          }

          // Wait before retrying (exponential backoff)
          const delay = Math.min(1000 * Math.pow(2, attempt - 1), 3000);
          console.log(`Attempt ${attempt} failed, retrying in ${delay}ms...`);

          // Update loading message to show retry
          this.updateLoadingMessage(attempt + 1);

          await new Promise((resolve) => setTimeout(resolve, delay));
        }
      }

      // Handle the final error after all retries
      if (lastError) {
        this.handleCalculationError(lastError, productData.quantity);
      }
    } catch (error) {
      // Catch any unexpected errors
      console.error("Unexpected error in calculateShipping:", error);
      this.displayError(
        "An unexpected error occurred. Please try again.",
        "general"
      );
    } finally {
      // Always ensure loading state is cleared
      this.setLoadingState(false);
    }
  }

  handleCalculationError(error, quantity) {
    console.error("Shipping calculation error after retries:", error);

    // Provide specific error messages based on error type and context
    if (error.name === "AbortError") {
      let timeoutMessage = "Request timed out. Please try again.";
      if (error.step) {
        const stepMessages = {
          clearing_cart: "Timed out while preparing cart. Please try again.",
          adding_to_cart:
            "Timed out while adding product to cart. Please try again.",
          fetching_rates:
            "Timed out while fetching shipping rates. Please try again.",
          cleanup:
            "Calculation completed but cleanup timed out. Results may still be valid.",
        };
        timeoutMessage = stepMessages[error.step] || timeoutMessage;
      }
      this.displayError(timeoutMessage, "timeout");
    } else if (error.message.includes("not available")) {
      this.displayError(
        "This product variant is currently not available.",
        "out_of_stock"
      );
    } else if (
      error.message.includes("Failed to fetch") ||
      error.message.includes("NetworkError") ||
      !navigator.onLine
    ) {
      this.displayError(
        "Network error occurred. Please check your internet connection and try again.",
        "api_error"
      );
    } else if (error.message.includes("Failed to add to cart")) {
      this.displayError(
        "Unable to add product to cart for shipping calculation. Please try again or contact support.",
        "api_error"
      );
    } else if (error.message.includes("Failed to get shipping rates")) {
      this.displayError(
        "Unable to retrieve shipping rates. Please verify the pincode and try again.",
        "api_error"
      );
    } else {
      // Generic error with helpful message
      let genericMessage =
        "Unable to calculate shipping rates after multiple attempts. Please try again in a moment.";
      if (error.step) {
        genericMessage += ` (Error occurred during ${error.step.replace(
          "_",
          " "
        )})`;
      }
      this.displayError(genericMessage, "api_error");
    }
  }

  getProductData() {
    // Get variant ID from product form
    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (!productForm) {
      console.warn("Product form not found");
      return null;
    }

    const variantInput = productForm.querySelector(
      'input[name="id"], select[name="id"]'
    );
    if (!variantInput) {
      console.warn("Variant input not found");
      return null;
    }

    const variantId = variantInput.value;
    if (!variantId) {
      console.warn("Variant ID is empty");
      return null;
    }

    // Try multiple ways to find the quantity input
    let quantityInput = null;
    let quantity = 50; // Default for bulk products

    // Method 1: Standard quantity input in product form
    quantityInput = productForm.querySelector('input[name="quantity"]');

    // Method 2: Look for quantity input anywhere on the page
    if (!quantityInput) {
      quantityInput = document.querySelector('input[name="quantity"]');
    }

    // Method 3: Look for quantity input with different selectors
    if (!quantityInput) {
      quantityInput = document.querySelector(
        ".quantity-input, [data-quantity-input], #quantity"
      );
    }

    // Method 4: Look for quantity selector/stepper components
    if (!quantityInput) {
      quantityInput = document.querySelector(
        "quantity-input input, .quantity-selector input"
      );
    }

    console.log("Quantity input search results:", {
      method1: productForm.querySelector('input[name="quantity"]'),
      method2: document.querySelector('input[name="quantity"]'),
      method3: document.querySelector(
        ".quantity-input, [data-quantity-input], #quantity"
      ),
      method4: document.querySelector(
        "quantity-input input, .quantity-selector input"
      ),
      finalInput: quantityInput,
    });

    if (quantityInput) {
      // Get the current value, ensuring it's fresh
      const rawValue = quantityInput.value;
      const inputValue = parseInt(rawValue);
      const minValue = parseInt(quantityInput.getAttribute("min")) || 50;
      const stepValue = parseInt(quantityInput.getAttribute("step")) || 1;

      console.log("🔍 Quantity input analysis:", {
        element: quantityInput,
        rawValue: rawValue,
        parsedValue: inputValue,
        minValue: minValue,
        stepValue: stepValue,
        isValid: !isNaN(inputValue) && inputValue >= minValue
      });

      // Use the actual input value if it's valid, otherwise use minimum
      if (!isNaN(inputValue) && inputValue >= minValue) {
        quantity = inputValue;
        console.log("✅ Using input value:", quantity);
      } else {
        quantity = minValue;
        console.log("⚠️ Input invalid, using minimum:", quantity);
      }

      // Additional validation for step increments
      if (stepValue > 1 && quantity % stepValue !== 0) {
        const adjustedQuantity = Math.ceil(quantity / stepValue) * stepValue;
        console.log(`📏 Adjusting quantity from ${quantity} to ${adjustedQuantity} (step: ${stepValue})`);
        quantity = adjustedQuantity;
      }

      console.log("📊 Final quantity decision:", {
        inputValue: rawValue,
        parsedValue: inputValue,
        minValue: minValue,
        stepValue: stepValue,
        finalQuantity: quantity,
      });
    } else {
      console.warn(
        "❌ Quantity input not found with any method, using default quantity of 50"
      );

      // Try to find quantity from any visible text or data attributes
      const quantityDisplay = document.querySelector(
        "[data-quantity], .quantity-display"
      );
      if (quantityDisplay) {
        const displayValue = parseInt(
          quantityDisplay.textContent || quantityDisplay.dataset.quantity
        );
        if (displayValue && displayValue > 0) {
          quantity = displayValue;
          console.log("📱 Found quantity from display element:", quantity);
        }
      }
    }

    console.log("Final product data:", {
      variantId,
      quantity,
      productType: this.getProductType(),
    });

    return {
      variantId: variantId,
      quantity: quantity, // Use exact quantity for shipping calculation
      productType: this.getProductType(),
    };
  }



  getProductType() {
    // Try to get product type from various possible sources
    const productTypeElement = document.querySelector("[data-product-type]");
    if (productTypeElement) {
      return productTypeElement.dataset.productType;
    }

    // Check product-info element
    const productInfo = document.querySelector(
      "product-info[data-product-type]"
    );
    if (productInfo) {
      return productInfo.dataset.productType;
    }

    // Fallback: check if it's in the product JSON
    const productJson = document.querySelector(
      'script[type="application/json"][data-product-json]'
    );
    if (productJson) {
      try {
        const product = JSON.parse(productJson.textContent);
        return product.type || product.product_type;
      } catch (e) {
        console.warn("Could not parse product JSON");
      }
    }

    // Check meta tags
    const productTypeMeta = document.querySelector('meta[name="product-type"]');
    if (productTypeMeta) {
      return productTypeMeta.content;
    }

    return null;
  }

  // Helper method to check if product is E-invitation
  isEInvitationProduct(productType = null) {
    // Method 1: Check product tags (most reliable)
    const productTags = this.getProductTags();
    if (productTags) {
      const tagsLower = productTags.toLowerCase();
      if (tagsLower.includes('e-invitation') || tagsLower.includes('einvitation')) {
        console.log('E-invitation detected via product tags:', productTags);
        return true;
      }
    }

    // Method 2: Check product type (fallback)
    const type = productType || this.getProductType();
    if (type) {
      const lowerType = type.toLowerCase();
      const isEInvite = (
        lowerType.includes("e-invitation") ||
        lowerType.includes("einvitation") ||
        lowerType.includes("e invitation")
      );
      if (isEInvite) {
        console.log('E-invitation detected via product type:', type);
        return true;
      }
    }

    return false;
  }

  // Get product tags from various sources
  getProductTags() {
    // Try to get tags from product JSON
    const productJson = document.querySelector('script[type="application/json"][data-product-json]');
    if (productJson) {
      try {
        const product = JSON.parse(productJson.textContent);
        if (product.tags) {
          return Array.isArray(product.tags) ? product.tags.join(', ') : product.tags;
        }
      } catch (e) {
        console.warn('Could not parse product JSON for tags');
      }
    }

    // Try to get tags from meta tags
    const tagsMeta = document.querySelector('meta[name="product-tags"]');
    if (tagsMeta) {
      return tagsMeta.content;
    }

    // Try to get tags from data attributes
    const productElement = document.querySelector('[data-product-tags]');
    if (productElement) {
      return productElement.dataset.productTags;
    }

    return null;
  }

  // Get province/state from pincode for better Shopify API compatibility
  getProvinceFromPincode(pincode) {
    // Map common pincode ranges to Indian states
    // This helps Shopify match shipping zones more accurately
    const pincodeMap = {
      11: "Delhi",
      12: "Haryana",
      13: "Punjab",
      14: "Punjab",
      15: "Punjab",
      16: "Chandigarh",
      17: "Himachal Pradesh",
      18: "Himachal Pradesh",
      19: "Jammu and Kashmir",
      20: "Uttarakhand",
      21: "Uttar Pradesh",
      22: "Uttar Pradesh",
      23: "Uttar Pradesh",
      24: "Uttar Pradesh",
      25: "Uttar Pradesh",
      26: "Uttar Pradesh",
      27: "Uttar Pradesh",
      28: "Uttar Pradesh",
      30: "Rajasthan",
      31: "Rajasthan",
      32: "Rajasthan",
      33: "Rajasthan",
      34: "Rajasthan",
      40: "Maharashtra",
      41: "Maharashtra",
      42: "Maharashtra",
      43: "Maharashtra",
      44: "Maharashtra",
      45: "Maharashtra",
      46: "Maharashtra",
      47: "Maharashtra",
      48: "Maharashtra",
      49: "Maharashtra",
      50: "Telangana",
      51: "Andhra Pradesh",
      52: "Andhra Pradesh",
      53: "Andhra Pradesh",
      56: "Karnataka",
      57: "Karnataka",
      58: "Karnataka",
      59: "Karnataka",
      60: "Tamil Nadu",
      61: "Tamil Nadu",
      62: "Tamil Nadu",
      63: "Tamil Nadu",
      64: "Tamil Nadu",
      67: "Kerala",
      68: "Kerala",
      69: "Kerala",
      70: "West Bengal",
      71: "West Bengal",
      72: "West Bengal",
      73: "West Bengal",
      74: "West Bengal",
      75: "West Bengal",
      76: "West Bengal",
      80: "Odisha",
      81: "Odisha",
      82: "Bihar",
      83: "Bihar",
      84: "Bihar",
      85: "Jharkhand",
      90: "Assam",
      91: "Assam",
      92: "Arunachal Pradesh",
      93: "Nagaland",
      94: "Manipur",
      95: "Mizoram",
      96: "Tripura",
    };

    const prefix = pincode.substring(0, 2);
    return pincodeMap[prefix] || "";
  }

  async fetchShippingRatesWithTempCart(variantId, quantity, pincode) {
    // Cancel any existing request
    if (this.currentRequest) {
      this.currentRequest.abort();
    }

    // Create abort controller for this request with timeout
    this.currentRequest = new AbortController();
    const signal = this.currentRequest.signal;

    // Set standard timeout for all requests
    const timeoutMs = 15000; // 15 seconds for all requests
    const timeoutId = setTimeout(() => {
      console.log(`Request timeout after ${timeoutMs}ms`);
      this.currentRequest.abort();
    }, timeoutMs);

    let step = "initialization";

    try {
      // Step 1: Ensure cart is empty before starting
      step = "clearing_cart";
      await this.ensureCartIsEmpty(signal);

      // Step 2: Add product to cart temporarily
      step = "adding_to_cart";
      await this.addToCartTemporarily(variantId, quantity, signal);

      // Step 3: Get shipping rates
      step = "fetching_rates";
      const rates = await this.getShippingRates(pincode, signal);

      // Step 4: Clean up cart completely
      step = "cleanup";
      await this.ensureCartIsEmpty(signal);

      return rates;
    } catch (error) {
      if (error.name === "AbortError") {
        console.log(
          `Shipping calculation was cancelled or timed out at step: ${step}`
        );

        // Create a more specific timeout error
        const timeoutError = new Error(
          `Request timed out during ${step.replace("_", " ")}`
        );
        timeoutError.name = "AbortError";
        timeoutError.step = step;
        throw timeoutError;
      }

      // Add context about which step failed
      error.step = step;

      // Always try to clean up cart on error
      try {
        await this.ensureCartIsEmpty();
      } catch (cleanupError) {
        console.error("Cart cleanup failed:", cleanupError);
      }

      throw error;
    } finally {
      clearTimeout(timeoutId);
      this.currentRequest = null;
    }
  }

  async ensureCartIsEmpty(signal) {
    try {
      // Get current cart
      const response = await fetch("/cart.js", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        signal,
      });

      if (!response.ok) {
        throw new Error("Failed to get cart");
      }

      const cart = await response.json();

      // If cart has items, clear them all
      if (cart.items && cart.items.length > 0) {
        const updates = {};
        cart.items.forEach((item) => {
          updates[item.key] = 0;
        });

        const clearResponse = await fetch("/cart/update.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ updates }),
          signal,
        });

        if (!clearResponse.ok) {
          throw new Error("Failed to clear cart");
        }
      }
    } catch (error) {
      if (error.name === "AbortError") {
        throw error;
      }
      console.error("Error ensuring cart is empty:", error);
      throw new Error("Failed to prepare cart for shipping calculation");
    }
  }

  async addToCartTemporarily(variantId, quantity, signal) {
    console.log(`🛒 Adding to cart temporarily: Variant=${variantId}, Quantity=${quantity}`);
    
    try {
      const requestBody = {
        id: variantId,
        quantity: quantity,
      };
      
      console.log("📦 Cart API request body:", requestBody);
      
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
        signal,
      });

      if (!response.ok) {
        const errorText = await response.text();

        // Handle specific errors
        if (response.status === 422) {
          try {
            const errorData = JSON.parse(errorText);
            if (errorData.message) {
              throw new Error(errorData.message);
            }
          } catch (parseError) {
            // If we can't parse the error, fall back to generic message
          }
        }

        throw new Error(`Failed to add to cart: ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      if (error.name === "AbortError") {
        throw error;
      }
      console.error("Error adding to cart temporarily:", error);

      // Provide more specific error messages
      if (error.message.includes("quantity")) {
        throw error; // Re-throw quantity-specific errors
      }

      throw new Error("Failed to add product to cart for shipping calculation");
    }
  }

  async getShippingRates(pincode, signal) {
    try {
      // First, verify cart has items (required for shipping calculation)
      const cartCheck = await fetch("/cart.js", { signal });
      const cart = await cartCheck.json();

      if (!cart.items || cart.items.length === 0) {
        console.warn("Cart is empty - shipping rates require items in cart");
        throw new Error("Cart must have items to calculate shipping rates");
      }

      console.log(
        "Cart has items for shipping calculation:",
        cart.items.length,
        "items"
      );

      // Use Shopify's shipping rates API with complete address information
      // Shopify requires country code and may need province/state for proper zone matching
      const params = new URLSearchParams({
        "shipping_address[zip]": pincode,
        "shipping_address[country]": "IN",
        "shipping_address[province]": this.getProvinceFromPincode(pincode),
        "shipping_address[city]": "",
        "shipping_address[address1]": "",
        "shipping_address[address2]": "",
        "shipping_address[company]": "",
        "shipping_address[first_name]": "Customer",
        "shipping_address[last_name]": "Name",
      });

      const response = await fetch(
        `/cart/shipping_rates.json?${params.toString()}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          signal,
        }
      );

      if (!response.ok) {
        // Log the response for debugging
        const responseText = await response.text();
        console.log(
          "Shipping rates API response:",
          response.status,
          responseText
        );

        if (response.status === 422) {
          // Try to parse error details
          try {
            const errorData = JSON.parse(responseText);
            console.log("Shipping rates error details:", errorData);

            // Check for specific error messages
            if (errorData.message && errorData.message.includes("shipping")) {
              console.log("Shipping configuration issue detected");
            }
          } catch (e) {
            console.log("Could not parse error response");
          }
          // Invalid address or no shipping available
          return [];
        }
        throw new Error(
          `Failed to get shipping rates: ${response.status} ${responseText}`
        );
      }

      const data = await response.json();
      console.log("Shipping rates response:", data);
      return data.shipping_rates || [];
    } catch (error) {
      if (error.name === "AbortError") {
        throw error;
      }
      console.error("Error getting shipping rates:", error);
      throw new Error("Failed to get shipping rates");
    }
  }

  setLoadingState(isLoading) {
    this.isCalculating = isLoading;

    const calculateBtn = this.container.querySelector(
      ".calculate-shipping-btn"
    );
    const resultsElement = this.container.querySelector(".shipping-results");

    if (calculateBtn) {
      calculateBtn.disabled = isLoading;
      const buttonSpan = calculateBtn.querySelector("span");
      if (buttonSpan) {
        buttonSpan.textContent = isLoading ? "Calculating..." : "Check";
      } else {
        calculateBtn.textContent = isLoading ? "Calculating..." : "Check";
      }

      // Add loading class for additional styling
      if (isLoading) {
        calculateBtn.classList.add("loading");
      } else {
        calculateBtn.classList.remove("loading");
      }
    }

    if (resultsElement) {
      if (isLoading) {
        // Clear previous results and show loading state
        resultsElement.innerHTML = this.getLoadingHTML();
      }
    }

    // Don't call updateCalculateButtonState when loading to prevent button state conflicts
    if (!isLoading) {
      this.updateCalculateButtonState();
    }
  }

  getLoadingHTML(attempt = 1) {
    const messages = {
      1: {
        main: "Checking delivery options...",
        sub: "This may take a few seconds",
      },
      2: {
        main: "Retrying shipping calculation...",
        sub: "Please wait while we try again",
      },
      3: {
        main: "Final attempt in progress...",
        sub: "Almost done, please be patient",
      },
    };

    const message = messages[attempt] || messages[1];

    return `
      <div class="shipping-loading" role="status" aria-live="polite">
        <div class="loading-content">
          <div class="loading-spinner" aria-hidden="true"></div>
          <div class="loading-text">
            <span class="loading-main">${message.main}</span>
            <small class="loading-sub">${message.sub}</small>
          </div>
        </div>
      </div>
    `;
  }

  updateLoadingMessage(attempt) {
    const resultsElement = this.container.querySelector(".shipping-results");
    if (resultsElement && this.isCalculating) {
      resultsElement.innerHTML = this.getLoadingHTML(attempt);
    }
  }

  displayResults(rates, pincode, quantity) {
    const resultsElement = this.container.querySelector(".shipping-results");
    if (!resultsElement) {
      return;
    }

    let html = `
      <div class="shipping-available">
        <h4>Delivery to ${pincode} (${quantity} items)</h4>
        <div class="shipping-options">
    `;

    rates.forEach((rate) => {
      const price = parseFloat(rate.price);
      const priceDisplay = price === 0 ? "FREE" : `₹${price.toFixed(2)}`;

      html += `
        <div class="shipping-option">
          <div class="shipping-method">${rate.name}</div>
          <div class="shipping-details">
            <span class="shipping-price">${priceDisplay}</span>
            ${
              rate.delivery_date
                ? `<span class="shipping-eta">Delivery by ${rate.delivery_date}</span>`
                : ""
            }
          </div>
        </div>
      `;
    });

    html += `
        </div>
      </div>
    `;

    resultsElement.innerHTML = html;
  }

  displayNoShippingMessage(pincode, calculationQuantity, rawQuantity = null) {
    const resultsElement = this.container.querySelector(".shipping-results");
    if (!resultsElement) {
      return;
    }

    // Use raw quantity for display if available, otherwise use calculation quantity
    const displayQuantity = rawQuantity || calculationQuantity;

    let message = `No delivery available to pincode ${pincode}`;
    let helpText = "";
    let debugInfo = "";

    // Add debug information if debug mode is enabled
    // if (window.simpleProductShippingIntegrationDebug) {
    //   debugInfo = `
    //     <div style="margin-top: 10px; padding: 10px; background: #f0f0f0; border-radius: 4px; font-size: 12px;">
    //       <strong>Debug Info:</strong><br>
    //       • Pincode: ${pincode}<br>
    //       • Quantity: ${displayQuantity} (calc: ${calculationQuantity})<br>
    //       • Check your Shopify Admin → Settings → Shipping and delivery<br>
    //       • Ensure shipping zones are configured for India<br>
    //       • Verify pincode ${pincode} is within your shipping zones<br>
    //       • Check browser console for API response details
    //     </div>
    //   `;
    // }

    // Generic message that covers all scenarios without making assumptions
    message += ".";
    helpText =
      "No shipping options are currently available. This could be due to location or quantity restrictions. Please contact support for assistance.";

    resultsElement.innerHTML = `
      <div class="shipping-unavailable" role="alert" aria-live="polite">
        <div class="unavailable-content">
          <span class="unavailable-icon" aria-hidden="true">📍</span>
          <div class="unavailable-message">
            <p class="unavailable-main">${message}</p>
            <small class="unavailable-help">${helpText}</small>
            ${debugInfo}
          </div>
        </div>

      </div>
    `;
  }

  displayError(message, errorType = "general") {
    const resultsElement = this.container.querySelector(".shipping-results");
    if (!resultsElement) {
      return;
    }

    // Clear any existing content
    resultsElement.innerHTML = "";

    // Create error element with appropriate styling based on error type
    const errorClass = this.getErrorClass(errorType);
    const errorIcon = this.getErrorIcon(errorType);

    resultsElement.innerHTML = `
      <div class="shipping-error ${errorClass}" role="alert" aria-live="assertive">
        <div class="error-content">
          ${errorIcon}
          <div class="error-message">
            <p>${message}</p>
            ${this.getErrorHelpText(errorType)}
          </div>
        </div>
      </div>
    `;

    // Scroll error into view if needed
    this.scrollToError();
  }

  getErrorClass(errorType) {
    const errorClasses = {
      invalid_pincode: "error-validation",
      no_shipping: "error-no-shipping",
      api_error: "error-api",
      timeout: "error-timeout",
      out_of_stock: "error-stock",
      general: "error-general",
    };
    return errorClasses[errorType] || errorClasses.general;
  }

  getErrorIcon(errorType) {
    const icons = {
      invalid_pincode: "⚠️",
      no_shipping: "📍",
      api_error: "🔄",
      timeout: "⏱️",
      out_of_stock: "❌",
      general: "⚠️",
    };
    return `<span class="error-icon" aria-hidden="true">${
      icons[errorType] || icons.general
    }</span>`;
  }

  getErrorHelpText(errorType) {
    const helpTexts = {
      invalid_pincode:
        '<small class="error-help">Please enter exactly 6 digits (e.g., 110001)</small>',
      no_shipping:
        '<small class="error-help">Try a different pincode or contact support for shipping options</small>',
      api_error:
        '<small class="error-help">Please check your internet connection and try again</small>',
      timeout:
        '<small class="error-help">The request took too long. Please try again</small>',
      out_of_stock:
        '<small class="error-help">Please select a different variant</small>',
      general:
        '<small class="error-help">If the problem persists, please contact support</small>',
    };
    return helpTexts[errorType] || helpTexts.general;
  }

  scrollToError() {
    const errorElement = this.container.querySelector(".shipping-error");
    if (errorElement) {
      // Smooth scroll to error with some offset
      const rect = errorElement.getBoundingClientRect();

      if (rect.top < 0 || rect.bottom > window.innerHeight) {
        errorElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }
  }

  // Public method to update calculator when variant changes
  updateVariant() {
    // Clear previous results when variant changes
    this.clearPreviousResults();

    // Update button state in case quantity rules changed
    this.updateCalculateButtonState();

    // Re-bind quantity events in case the form structure changed
    this.bindQuantityChangeEvents();
  }

  // Public method to update calculator when quantity changes
  updateQuantity() {
    // Clear previous results when quantity changes
    this.clearPreviousResults();
  }

  // Public method to destroy the calculator
  destroy() {
    if (this.currentRequest) {
      this.currentRequest.abort();
    }

    // Remove event listeners
    const form = this.container?.querySelector(".simple-shipping-form");
    const pincodeInput = this.container?.querySelector(".pincode-input");
    const calculateBtn = this.container?.querySelector(
      ".calculate-shipping-btn"
    );

    if (form) {
      form.removeEventListener("submit", this.calculateShipping);
    }
    if (pincodeInput) {
      pincodeInput.removeEventListener("input", this.handlePincodeInput);
    }
    if (calculateBtn) {
      calculateBtn.removeEventListener("click", this.calculateShipping);
    }
  }
}

// Auto-initialize when DOM is ready
document.addEventListener("DOMContentLoaded", function () {
  const shippingCalculatorElement = document.getElementById(
    "simple-product-shipping-calculator"
  );
  if (shippingCalculatorElement) {
    window.simpleProductShippingCalculator =
      new SimpleProductShippingCalculator("simple-product-shipping-calculator");

    // Listen for custom events that might be triggered by other scripts
    document.addEventListener("product:quantity-changed", function () {
      if (window.simpleProductShippingCalculator) {
        window.simpleProductShippingCalculator.updateQuantity();
      }
    });

    document.addEventListener("product:variant-changed", function () {
      if (window.simpleProductShippingCalculator) {
        window.simpleProductShippingCalculator.updateVariant();
      }
    });
  }
});

// Export for module systems
if (typeof module !== "undefined" && module.exports) {
  module.exports = SimpleProductShippingCalculator;
}
